import React from 'react';
import { Link, useLocation } from 'react-router-dom'; // 1. useLocation hook'unu import ettik
import { useLanguage } from '../context/LanguageContext';
import { Github, MessageCircle, ExternalLink, Shield } from 'lucide-react';
import { getLanguageFromPath, localizedPath, routeIdFromPath } from '../utils/routes';

const Footer = () => {
    const { t, language } = useLanguage();
    const currentYear = new Date().getFullYear();
    const location = useLocation(); // 2. Mevcut URL bilgisini aldık
    const activeLanguage = getLanguageFromPath(location.pathname) || language || 'en';
    const legalLabels = activeLanguage === 'tr'
        ? {
            'privacy-policy': 'Gizlilik Politikası',
            'terms-of-service': 'Kullanım Şartları',
            about: 'Hakkımızda',
            contact: 'İletişim',
        }
        : {
            'privacy-policy': 'Privacy Policy',
            'terms-of-service': 'Terms of Service',
            about: 'About Us',
            contact: 'Contact',
        };
    const legalLinks = ['privacy-policy', 'terms-of-service', 'about', 'contact'];

    const shouldHideFooter = ['story', 'hideout', 'kappa-path'].includes(routeIdFromPath(location.pathname));

    // 5. Eğer gizlenmesi gereken bir sayfadaysak hiçbir şey render etme (boş dön)
    if (shouldHideFooter) {
        return null;
    }

    return (
        <footer className="bg-[#030303]/95 border-t border-zinc-800/60 py-10 px-6 mt-auto relative overflow-hidden">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(16,185,129,0.03),transparent_50%)] pointer-events-none"></div>

            <div className="max-w-[1600px] mx-auto relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-10">

                    {/* Logo & Slogan */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#09090b] border border-zinc-700 w-8 h-8 rounded-sm p-1.5 flex flex-col gap-0.5">
                                <div className="flex gap-0.5 h-full"><div className="bg-zinc-700 w-full"></div><div className="bg-emerald-500 w-full shadow-[0_0_8px_rgba(16,185,129,0.6)]"></div></div>
                                <div className="flex gap-0.5 h-full"><div className="bg-zinc-700 w-full"></div><div className="bg-zinc-700 w-full"></div></div>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-white font-black text-base tracking-tighter uppercase leading-none">TARKOVSTASH</span>
                                <span className="text-emerald-600 text-[9px] font-bold tracking-widest leading-none text-right">.COM</span>
                            </div>
                        </div>
                        <p className="text-zinc-500 text-xs leading-relaxed max-w-xs">
                            {t('footer_slogan')}
                        </p>
                        <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_6px_rgba(16,185,129,0.8)]"></div>
                            <span className="text-emerald-500/70 text-[9px] font-mono font-bold uppercase tracking-widest">{t('footer_status')}</span>
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div className="space-y-4">
                        <h4 className="text-zinc-300 font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                            <ExternalLink className="w-3 h-3 text-emerald-500" />
                            {t('footer_resources')}
                        </h4>
                        <ul className="space-y-2.5 text-zinc-500 text-xs">
                            <li>
                                <a href="https://escapefromtarkov.fandom.com/wiki/Escape_from_Tarkov_Wiki" target="_blank" rel="noreferrer" className="hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                                    <span className="w-1 h-1 bg-zinc-700 rounded-full group-hover:bg-emerald-500 transition-colors"></span>
                                    Tarkov Wiki
                                </a>
                            </li>
                            <li>
                                <a href="https://tarkov.dev/" target="_blank" rel="noreferrer" className="hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                                    <span className="w-1 h-1 bg-zinc-700 rounded-full group-hover:bg-emerald-500 transition-colors"></span>
                                    Tarkov.dev API
                                </a>
                            </li>
                            <li>
                                <a href="https://www.escapefromtarkov.com/" target="_blank" rel="noreferrer" className="hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                                    <span className="w-1 h-1 bg-zinc-700 rounded-full group-hover:bg-emerald-500 transition-colors"></span>
                                    {t('footer_official_site')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Community */}
                    <div className="space-y-4">
                        <h4 className="text-zinc-300 font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                            <MessageCircle className="w-3 h-3 text-emerald-500" />
                            {t('footer_community')}
                        </h4>
                        <ul className="space-y-2.5 text-zinc-500 text-xs">
                            <li>
                                <a href="https://github.com/BalyaEy" target="_blank" rel="noreferrer" className="hover:text-white transition-colors flex items-center gap-2 group">
                                    <Github className="w-3.5 h-3.5 text-zinc-600 group-hover:text-white transition-colors" />
                                    GitHub
                                </a>
                            </li>
                            <li>
                                <a href="https://discord.gg/6fnEzwqybZ" target="_blank" rel="noreferrer" className="hover:text-[#5865F2] transition-colors flex items-center gap-2 group">
                                    <MessageCircle className="w-3.5 h-3.5 text-zinc-600 group-hover:text-[#5865F2] transition-colors" />
                                    Discord
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Legal Links */}
                    <div className="space-y-4">
                        <h4 className="text-zinc-300 font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                            <Shield className="w-3 h-3 text-red-500/70" />
                            {t('footer_legal')}
                        </h4>
                        <ul className="space-y-2.5 text-zinc-500 text-xs">
                            {legalLinks.map((routeId) => (
                                <li key={routeId}>
                                    <Link to={localizedPath(routeId, activeLanguage)} className="hover:text-emerald-400 transition-colors flex items-center gap-2 group">
                                        <span className="w-1 h-1 bg-zinc-700 rounded-full group-hover:bg-emerald-500 transition-colors"></span>
                                        {legalLabels[routeId]}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="text-xs text-gray-500 text-center max-w-4xl mx-auto mb-4 leading-relaxed">
                    <p>{t('footer_disclaimer')}</p>
                    <p>{t('footer_data_notice')}</p>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-zinc-900/80 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-zinc-600 text-[10px] font-mono tracking-widest uppercase">
                        © {currentYear} TARKOVSTASH.COM — {t('footer_rights')}
                    </p>
                    <div className="flex items-center gap-4">
                        <span className="text-zinc-800 text-[9px] font-mono tracking-wider">v2.0.0</span>
                        <div className="w-px h-3 bg-zinc-800"></div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
